import React, { useState, useEffect, useCallback } from "react";
import { Dialog, Portal, CloseButton } from "@chakra-ui/react";
import {
  Button,
  Input,
  Flex,
  Text,
  Box,
  Switch,
  Checkbox,
} from "@chakra-ui/react";
import { Tabs } from "@chakra-ui/react";
import { SquareCheck, User } from "lucide-react";
import { CategoryCombobox } from "@/components/shared/category-combobox";
import { PropertySelectMenu } from "@/components/shared/property-select-menu";
import { CategorizationRule } from "@/services/categorization-rules";
import { toaster } from "@/components/shared/atoms/toaster";
import { Transaction } from "@/stores/transactions-store/transactions-reducer";
import { formatCurrency } from "@/lib/utils";
import { categories } from "@/lib/constants";

interface CategorizationRuleModalProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  rule?: CategorizationRule;
  organizationId: string;
  onSave: (rule: CategorizationRule) => void;
  isEditing?: boolean;
  transactions?: Transaction[];
}

export function CategorizationRuleModal({
  showModal,
  setShowModal,
  rule,
  organizationId,
  onSave,
  isEditing = false,
  transactions = [],
}: CategorizationRuleModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    namePattern: "",
    amountMin: "",
    amountMax: "",
    amountExact: "",
    category: "",
    priority: 0,
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [applyToMatching, setApplyToMatching] = useState(false);
  // Individual toggle states - initialize to false, will be set properly in useEffect
  const [namePatternEnabled, setNamePatternEnabled] = useState(false);
  const [amountEnabled, setAmountEnabled] = useState(false);
  const [updateCategoryEnabled, setUpdateCategoryEnabled] = useState(true);
  const [updatePropertyEnabled, setUpdatePropertyEnabled] = useState(false);
  const [propertyId, setPropertyId] = useState<string>("");

  // State to track selected transactions in preview
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    string[]
  >([]);

  // Add this helper near the top of the component, after selectedTransactionIds state
  const handleSelectionChange = (id: string, checked: boolean) => {
    setSelectedTransactionIds((prev) =>
      checked ? [...prev, id] : prev.filter((tid) => tid !== id),
    );
  };

  // Initialize form data when modal opens or rule changes
  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name,
        description: rule.description || "",
        namePattern: rule.namePattern || "",
        amountMin: rule.amountMin ? Number(rule.amountMin).toString() : "",
        amountMax: rule.amountMax ? Number(rule.amountMax).toString() : "",
        amountExact: rule.amountExact
          ? Number(rule.amountExact).toString()
          : "",
        category: rule.category,
        priority: rule.priority,
        isActive: rule.isActive,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        namePattern: "",
        amountMin: "",
        amountMax: "",
        amountExact: "",
        category: "",
        priority: 0,
        isActive: true,
      });
    }
  }, [rule, showModal]);

  // Reset toggle states when modal opens or rule changes
  useEffect(() => {
    if (showModal) {
      // Set toggle states based on current rule or defaults
      setNamePatternEnabled(rule ? !!rule.namePattern : true);
      setAmountEnabled(
        rule ? !!(rule.amountMin || rule.amountMax || rule.amountExact) : false,
      );
      setUpdateCategoryEnabled(rule ? !!rule.category : true);
      setUpdatePropertyEnabled(false);
      setPropertyId("");
    }
  }, [rule, showModal]);

  // Memoized filtered transactions (matching and not already categorized)
  const filteredTransactions = React.useMemo(() => {
    return transactions.filter((t) => {
      let match = true;
      if (namePatternEnabled) {
        if (!formData.namePattern) {
          return false;
        }
        try {
          const regex = new RegExp(formData.namePattern, "i");
          match = match && regex.test(t.name || "");
        } catch {
          return false;
        }
      }
      if (amountEnabled) {
        const amount = Math.abs(Number(t.amount));
        if (formData.amountExact) {
          match = match && amount === Number(formData.amountExact);
        } else {
          if (formData.amountMin) {
            match = match && amount >= Number(formData.amountMin);
          }
          if (formData.amountMax) {
            match = match && amount <= Number(formData.amountMax);
          }
        }
      }
      if (
        updateCategoryEnabled &&
        formData.category &&
        t.category === formData.category
      ) {
        return false;
      }
      return match;
    });
  }, [
    transactions,
    namePatternEnabled,
    formData.namePattern,
    amountEnabled,
    formData.amountExact,
    formData.amountMin,
    formData.amountMax,
    updateCategoryEnabled,
    formData.category,
  ]);

  // Keep selectedTransactionIds in sync with filtered transactions, but only update if changed
  useEffect(() => {
    const newIds = filteredTransactions.map((t) => t.id);
    if (
      newIds.length !== selectedTransactionIds.length ||
      !newIds.every((id, i) => id === selectedTransactionIds[i])
    ) {
      setSelectedTransactionIds(newIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions]);

  // Calculate matching transactions count
  const matchingCount = selectedTransactionIds.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organizationId) {
      toaster.create({
        description: "Organization not available. Please try again.",
        type: "error",
        duration: 3000,
        closable: true,
      });
      return;
    }

    if (!validateForm()) {
      toaster.create({
        description: "Please fill in all required fields correctly.",
        type: "error",
        duration: 3000,
        closable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Use PUT only if editing (rule && rule.id), otherwise POST
      const isEdit = rule && rule.id;
      const url = isEdit
        ? `/api/categorization-rules/${rule.id}?organizationId=${organizationId}`
        : `/api/categorization-rules?organizationId=${organizationId}`;
      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || undefined,
          namePattern: namePatternEnabled
            ? formData.namePattern || undefined
            : undefined,
          amountMin:
            amountEnabled && formData.amountMin
              ? Number(formData.amountMin)
              : undefined,
          amountMax:
            amountEnabled && formData.amountMax
              ? Number(formData.amountMax)
              : undefined,
          amountExact:
            amountEnabled && formData.amountExact
              ? Number(formData.amountExact)
              : undefined,
          category: updateCategoryEnabled ? formData.category : "",
          priority: formData.priority,
          isActive: formData.isActive,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save rule");
      }

      const data = await response.json();
      onSave(data.rule);
      setShowModal(false);

      toaster.create({
        description: `Rule ${isEditing ? "updated" : "created"} successfully`,
        type: "success",
        duration: 3000,
        closable: true,
      });

      if (applyToMatching) {
        try {
          // Filter transactions using the same logic as the preview
          const matchingTransactions = transactions.filter((t) =>
            selectedTransactionIds.includes(t.id),
          );

          const requestBody = {
            rule: {
              namePattern: namePatternEnabled
                ? formData.namePattern
                : undefined,
              amountMin:
                amountEnabled && formData.amountMin
                  ? Number(formData.amountMin)
                  : undefined,
              amountMax:
                amountEnabled && formData.amountMax
                  ? Number(formData.amountMax)
                  : undefined,
              amountExact:
                amountEnabled && formData.amountExact
                  ? Number(formData.amountExact)
                  : undefined,
              category: updateCategoryEnabled ? formData.category : undefined,
            },
            transactions: matchingTransactions,
          };

          const applyRes = await fetch(
            `/api/categorization-rules/apply-single?organizationId=${organizationId}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestBody),
            },
          );
          if (!applyRes.ok) {
            const error = await applyRes.json();
            toaster.create({
              description:
                error.error || "Failed to apply rule to transactions.",
              type: "error",
              duration: 4000,
              closable: true,
            });
          } else {
            const data = await applyRes.json();
            toaster.create({
              description: `Rule applied to ${data.categorizedCount ?? 0} transaction${data.categorizedCount === 1 ? "" : "s"}.`,
              type: "success",
              duration: 4000,
              closable: true,
            });
          }
        } catch (err) {
          toaster.create({
            description: "Failed to apply rule to transactions.",
            type: "error",
            duration: 4000,
            closable: true,
          });
        }
      }
    } catch (error) {
      console.error("Error saving rule:", error);
      toaster.create({
        description:
          error instanceof Error ? error.message : "Failed to save rule",
        type: "error",
        duration: 3000,
        closable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    field: string,
    value: string | number | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) return false;

    // Only require category if updateCategoryEnabled is true
    if (updateCategoryEnabled && !(formData.category || "")) return false;

    // Validate regex pattern if provided and enabled
    if (namePatternEnabled && formData.namePattern) {
      try {
        new RegExp(formData.namePattern);
      } catch {
        return false;
      }
    }

    return true;
  };

  // Handle toggle changes and clear data when disabled
  const handleNamePatternToggle = (details: { checked: boolean }) => {
    setNamePatternEnabled(details.checked);
    if (!details.checked) {
      handleInputChange("namePattern", "");
    }
  };

  const handleAmountToggle = (details: { checked: boolean }) => {
    setAmountEnabled(details.checked);
    if (!details.checked) {
      handleInputChange("amountMin", "");
      handleInputChange("amountMax", "");
      handleInputChange("amountExact", "");
    }
  };

  const handleUpdateCategoryToggle = (details: { checked: boolean }) => {
    setUpdateCategoryEnabled(details.checked);
    if (!details.checked) {
      handleInputChange("category", "");
    }
  };

  const handleUpdatePropertyToggle = (details: { checked: boolean }) => {
    setUpdatePropertyEnabled(details.checked);
    if (!details.checked) {
      setPropertyId("");
    }
  };

  // Emergency close function
  const emergencyClose = useCallback(() => {
    console.log("Emergency close triggered");
    setShowModal(false);
  }, [setShowModal]);

  // Add emergency close on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        emergencyClose();
      }
    };

    if (showModal) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [showModal, emergencyClose]);

  if (!showModal) {
    return null;
  }

  // Helper to get category label from value
  function getCategoryLabel(categoryValue: string | undefined): string {
    const value = categoryValue || "";
    for (const category of categories) {
      const subcategory = category.subcategories.find(
        (sub) => sub.value === value,
      );
      if (subcategory) {
        return `${category.label} > ${subcategory.label}`;
      }
    }
    return value;
  }

  // Helper to format date nicely
  function prettyDate(dateString: string): string {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  return (
    <Dialog.Root open={showModal} onOpenChange={(d) => setShowModal(d.open)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content minW='900px' maxW='1200px' maxH='90vh' p={0}>
            <Dialog.Header>
              <Dialog.Title>
                {isEditing ? "Edit rule" : "Create rule"}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body p={0}>
              <Box
                px={8}
                py={6}
                minHeight='500px'
                maxHeight='800px'
                display='flex'
                flexDirection='column'
                bg='chakra-body-bg'
              >
                <Tabs.Root defaultValue='settings'>
                  <Tabs.List>
                    <Tabs.Trigger value='settings'>
                      <User style={{ marginRight: 8 }} />
                      Settings
                    </Tabs.Trigger>
                    <Tabs.Trigger
                      value='preview'
                      disabled={applyToMatching ? matchingCount === 0 : false}
                    >
                      <SquareCheck style={{ marginRight: 8 }} />
                      Preview changes
                      {applyToMatching ? ` (${matchingCount})` : " (0)"}
                    </Tabs.Trigger>
                  </Tabs.List>
                  <Tabs.Content value='settings'>
                    <form onSubmit={handleSubmit}>
                      {/* Rule Name input at the top */}
                      <Box mb={6}>
                        <Text fontWeight='medium' mb={1}>
                          Rule Name
                        </Text>
                        <Input
                          type='text'
                          placeholder='e.g., Walmart Groceries'
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          required
                        />
                      </Box>
                      <Flex
                        gap={8}
                        align='flex-start'
                        direction={{ base: "column", md: "row" }}
                      >
                        {/* Left: Criteria */}
                        <Box flex='1' minW='320px'>
                          <Text fontWeight='bold' fontSize='lg' mb={4}>
                            If transaction matches criteria...
                          </Text>
                          <Box
                            mb={4}
                            p={4}
                            borderRadius='md'
                            borderWidth={1}
                            bg='chakra-subtle-bg'
                          >
                            <Flex align='center' mb={2} gap={2}>
                              <Switch.Root
                                checked={namePatternEnabled}
                                onCheckedChange={handleNamePatternToggle}
                                mr={2}
                              >
                                <Switch.HiddenInput />
                                <Switch.Control />
                              </Switch.Root>
                              <Text fontWeight='medium'>Merchant Name</Text>
                            </Flex>
                            <Input
                              type='text'
                              placeholder='e.g., .*GROCERY.* or .*WALMART.*'
                              value={formData.namePattern}
                              onChange={(e) =>
                                handleInputChange("namePattern", e.target.value)
                              }
                              disabled={!namePatternEnabled}
                              mb={2}
                            />
                            <Text fontSize='sm' color='chakra-subtle-text'>
                              Regular expression to match transaction names.
                            </Text>
                          </Box>
                          <Box
                            mb={4}
                            p={4}
                            borderRadius='md'
                            borderWidth={1}
                            bg='chakra-subtle-bg'
                          >
                            <Flex align='center' mb={2} gap={2}>
                              <Switch.Root
                                checked={amountEnabled}
                                onCheckedChange={handleAmountToggle}
                                mr={2}
                              >
                                <Switch.HiddenInput />
                                <Switch.Control />
                              </Switch.Root>
                              <Text fontWeight='medium'>Amount</Text>
                            </Flex>
                            <Flex gap={2}>
                              <Input
                                type='number'
                                placeholder='Min'
                                value={formData.amountMin}
                                onChange={(e) =>
                                  handleInputChange("amountMin", e.target.value)
                                }
                                disabled={!amountEnabled}
                              />
                              <Input
                                type='number'
                                placeholder='Max'
                                value={formData.amountMax}
                                onChange={(e) =>
                                  handleInputChange("amountMax", e.target.value)
                                }
                                disabled={!amountEnabled}
                              />
                              <Input
                                type='number'
                                placeholder='Exact'
                                value={formData.amountExact}
                                onChange={(e) =>
                                  handleInputChange(
                                    "amountExact",
                                    e.target.value,
                                  )
                                }
                                disabled={!amountEnabled}
                              />
                            </Flex>
                          </Box>
                        </Box>
                        {/* Right: Updates */}
                        <Box flex='1' minW='320px'>
                          <Text fontWeight='bold' fontSize='lg' mb={4}>
                            Then apply these updates...
                          </Text>
                          <Box
                            mb={4}
                            p={4}
                            borderRadius='md'
                            borderWidth={1}
                            bg='chakra-subtle-bg'
                          >
                            <Flex align='center' mb={2} gap={2}>
                              <Switch.Root
                                checked={updateCategoryEnabled}
                                onCheckedChange={handleUpdateCategoryToggle}
                                mr={2}
                              >
                                <Switch.HiddenInput />
                                <Switch.Control />
                              </Switch.Root>
                              <Text fontWeight='medium'>Update category</Text>
                            </Flex>
                            <Box
                              opacity={updateCategoryEnabled ? 1 : 0.5}
                              pointerEvents={
                                updateCategoryEnabled ? "auto" : "none"
                              }
                            >
                              <CategoryCombobox
                                value={formData.category || ""}
                                onChange={(value) =>
                                  handleInputChange(
                                    "category",
                                    (value as string) || "",
                                  )
                                }
                                multiple={false}
                                placeholder='Select a category'
                              />
                            </Box>
                          </Box>
                          <Box
                            mb={4}
                            p={4}
                            borderRadius='md'
                            borderWidth={1}
                            bg='chakra-subtle-bg'
                          >
                            <Flex align='center' mb={2} gap={2}>
                              <Switch.Root
                                checked={updatePropertyEnabled}
                                onCheckedChange={handleUpdatePropertyToggle}
                                mr={2}
                              >
                                <Switch.HiddenInput />
                                <Switch.Control />
                              </Switch.Root>
                              <Text fontWeight='medium'>Update property</Text>
                            </Flex>
                            <Box
                              opacity={updatePropertyEnabled ? 1 : 0.5}
                              pointerEvents={
                                updatePropertyEnabled ? "auto" : "none"
                              }
                            >
                              <PropertySelectMenu
                                value={String(propertyId)}
                                onValueChange={(value: string | undefined) =>
                                  setPropertyId(value ?? "")
                                }
                                showAllOption={false}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Flex>
                      {/* Apply to existing transactions */}
                      <Flex align='center' mt={6} gap={2}>
                        <input
                          type='checkbox'
                          id='applyToMatching'
                          checked={applyToMatching}
                          onChange={(e) => setApplyToMatching(e.target.checked)}
                          disabled={matchingCount === 0}
                          style={{ width: 18, height: 18 }}
                        />
                        <label
                          htmlFor='applyToMatching'
                          style={{
                            userSelect: "none",
                            opacity: matchingCount === 0 ? 0.5 : 1,
                          }}
                        >
                          Apply to {matchingCount} transaction
                          {matchingCount === 1 ? "" : "s"} now
                          {matchingCount === 0
                            ? " (no matching transactions)"
                            : ""}
                        </label>
                      </Flex>
                      <Flex justify='flex-end' gap={2} mt={6}>
                        <Dialog.ActionTrigger asChild>
                          <Button
                            variant='outline'
                            colorScheme='gray'
                            mr={2}
                            onClick={() => setShowModal(false)}
                          >
                            Cancel
                          </Button>
                        </Dialog.ActionTrigger>
                        <Button
                          type='submit'
                          variant='solid'
                          colorScheme='blue'
                          loading={loading}
                          disabled={!validateForm()}
                        >
                          Save
                        </Button>
                      </Flex>
                    </form>
                  </Tabs.Content>
                  <Tabs.Content value='preview'>
                    {/* Preview changes tab */}
                    {applyToMatching ? (
                      matchingCount > 0 ? (
                        <Box>
                          <Text fontWeight='medium' mb={4}>
                            The following transactions will be updated:
                          </Text>
                          <Box
                            maxH='400px'
                            overflowY='auto'
                            bg='chakra-body-bg'
                          >
                            {(() => {
                              if (filteredTransactions.length === 0) {
                                return (
                                  <Text color='chakra-subtle-text'>
                                    No transactions matched the criteria.
                                  </Text>
                                );
                              }
                              return filteredTransactions.map(
                                (t: Transaction) => {
                                  const checked =
                                    selectedTransactionIds.includes(t.id);
                                  // Determine if name/category will change
                                  const willUpdateName =
                                    updateCategoryEnabled &&
                                    formData.namePattern &&
                                    t.name !== formData.namePattern;
                                  const willUpdateCategory =
                                    updateCategoryEnabled &&
                                    formData.category &&
                                    t.category !== formData.category;
                                  return (
                                    <Flex
                                      key={t.id}
                                      align='center'
                                      justify='space-between'
                                      py={3}
                                      px={2}
                                      borderBottomWidth={1}
                                      borderColor='chakra-border-color'
                                      bg='chakra-subtle-bg'
                                      gap={2}
                                    >
                                      <Checkbox.Root
                                        checked={checked}
                                        onCheckedChange={(changes) => {
                                          handleSelectionChange(
                                            t.id,
                                            !!changes.checked,
                                          );
                                        }}
                                        mr={3}
                                        aria-label={`Select transaction ${t.name}`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Checkbox.HiddenInput />
                                        <Checkbox.Control />
                                      </Checkbox.Root>
                                      <Box minW='300px' flex='0 0 350px'>
                                        <Text
                                          fontSize='sm'
                                          color='chakra-subtle-text'
                                          mb={1}
                                        >
                                          {prettyDate(t.date || "")}
                                        </Text>
                                        <Flex align='center' gap={2}>
                                          {willUpdateName ? (
                                            <>
                                              <Text
                                                as='s'
                                                color='chakra-disabled-text'
                                                fontWeight='medium'
                                              >
                                                {t.name}
                                              </Text>
                                              <Text
                                                color='orange.400'
                                                fontWeight='bold'
                                              >
                                                →
                                              </Text>
                                              <Text
                                                color='orange.500'
                                                fontWeight='bold'
                                              >
                                                {formData.namePattern}
                                              </Text>
                                            </>
                                          ) : (
                                            <Text fontWeight='medium'>
                                              {t.name}
                                            </Text>
                                          )}
                                        </Flex>
                                      </Box>
                                      <Box
                                        minW='320px'
                                        flex='0 0 400px'
                                        display='flex'
                                        alignItems='center'
                                        gap={2}
                                      >
                                        {willUpdateCategory ? (
                                          <>
                                            <Text
                                              as='s'
                                              color='gray.400'
                                              _dark={{ color: "gray.500" }}
                                              fontWeight='medium'
                                            >
                                              {getCategoryLabel(
                                                t.category || "",
                                              )}
                                            </Text>
                                            <Text
                                              color='orange.400'
                                              _dark={{ color: "orange.300" }}
                                              fontWeight='bold'
                                            >
                                              →
                                            </Text>
                                            <Text
                                              color='orange.500'
                                              _dark={{ color: "orange.300" }}
                                              fontWeight='bold'
                                            >
                                              {getCategoryLabel(
                                                formData.category || "",
                                              )}
                                            </Text>
                                          </>
                                        ) : (
                                          <Text
                                            fontWeight='medium'
                                            color='chakra-body-text'
                                          >
                                            {getCategoryLabel(t.category || "")}
                                          </Text>
                                        )}
                                      </Box>
                                      <Box
                                        minW='100px'
                                        flex='0 0 100px'
                                        textAlign='right'
                                      >
                                        <Text
                                          fontWeight='bold'
                                          color='chakra-body-text'
                                        >
                                          {formatCurrency(Number(t.amount), 2)}
                                        </Text>
                                      </Box>
                                    </Flex>
                                  );
                                },
                              );
                            })()}
                          </Box>
                        </Box>
                      ) : (
                        <Text color='chakra-subtle-text'>
                          No transactions to update.
                        </Text>
                      )
                    ) : (
                      <Text color='chakra-subtle-text'>
                        To preview which transactions will be updated, select
                        the checkbox in the Settings tab to apply this rule now.
                      </Text>
                    )}
                  </Tabs.Content>
                </Tabs.Root>
              </Box>
            </Dialog.Body>
            <Dialog.CloseTrigger asChild>
              <CloseButton size='sm' position='absolute' top={2} right={2} />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
